import React from "react"
import { FileInput } from "react-admin"

import Link from "@material-ui/core/Link"

import {
  apiUploads,
  DURATIONS,
  STYLES,
  MAC_GROUPS,
  ShortFileField
} from "./workoutConstants"

// ── read-only matrix view for SHOW page ──

const cellStyle: React.CSSProperties = {
  border: "1px solid #ddd",
  padding: "6px 10px",
  fontSize: "0.8rem",
  textAlign: "center"
}

export const ZoneMatrixView: React.FC<{ cells: any[] }> = ({ cells }) => {
  const find = (mac: string, dur: number, style: string) =>
    cells.find(
      (c) => c.mac_group === mac && c.duration === dur && c.style === style
    )
  return (
    <div>
      {MAC_GROUPS.map((mg) => (
        <div key={mg.key} style={{ marginBottom: 16 }}>
          <strong>{mg.label}</strong>
          <table style={{ borderCollapse: "collapse", marginTop: 6 }}>
            <thead>
              <tr>
                <th style={cellStyle}></th>
                {STYLES.map((s) => (
                  <th key={s.key} style={cellStyle}>
                    {s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DURATIONS.map((d) => (
                <tr key={d}>
                  <td style={cellStyle}>
                    <strong>{d} min</strong>
                  </td>
                  {STYLES.map((s) => {
                    const cell = find(mg.key, d, s.key)
                    return (
                      <td key={s.key} style={cellStyle}>
                        {cell ? (
                          <Link
                            href={`${apiUploads}/${cell.filename}`}
                            target="_blank"
                            style={{ fontSize: "0.7rem" }}
                          >
                            file
                          </Link>
                        ) : (
                          <span style={{ color: "#aaa" }}>—</span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  )
}

// ── editable matrix styles ──

const matrixWrap: React.CSSProperties = {
  display: "flex",
  flexDirection: "row",
  gap: 16,
  width: "100%",
  maxWidth: "100%",
  marginTop: 8,
  boxSizing: "border-box",
  alignItems: "flex-start",
  overflow: "hidden"
}

const matrixCard: React.CSSProperties = {
  flex: "1 1 0",
  minWidth: 0,
  maxWidth: "50%",
  border: "1px solid #ececec",
  borderRadius: 10,
  padding: 18,
  background: "#fff",
  boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  overflow: "hidden",
  boxSizing: "border-box"
}

const matrixCardTitle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontWeight: 700,
  fontSize: "0.85rem",
  letterSpacing: 0.5,
  color: "#2b2b2b",
  textTransform: "uppercase",
  borderBottom: "1px solid #eee",
  paddingBottom: 10,
  marginBottom: 14
}

const matrixDot: React.CSSProperties = {
  width: 9,
  height: 9,
  borderRadius: "50%",
  background: "#F26722",
  display: "inline-block"
}

const durLabel: React.CSSProperties = {
  fontSize: "0.72rem",
  fontWeight: 700,
  color: "#9a9a9a",
  letterSpacing: 0.5,
  margin: "14px 0 8px"
}

const cellRow: React.CSSProperties = {
  display: "flex",
  gap: 6,
  width: "100%",
  maxWidth: "100%",
  overflow: "hidden",
  boxSizing: "border-box"
}

const cellBox: React.CSSProperties = {
  flex: "1 1 0",
  border: "1px solid #e6e6e6",
  borderRadius: 8,
  padding: "10px 11px",
  minWidth: 0,
  maxWidth: "33.33%",
  minHeight: 120,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  boxSizing: "border-box"
}

const cellStyleName: React.CSSProperties = {
  fontWeight: 700,
  fontSize: "0.78rem",
  color: "#2b2b2b",
  marginBottom: 2
}

// ── MatrixCard component ──

const MatrixCard: React.FC<{
  macKey: string
  macLabel: string
  existingCells?: any[]
  formData?: any
}> = ({ macKey, macLabel, existingCells, formData }) => {
  const findExisting = (d: number, style: string) =>
    (existingCells || []).find(
      (c) => c.mac_group === macKey && c.duration === d && c.style === style
    )
  return (
    <div style={matrixCard}>
      <div style={matrixCardTitle}>
        <span style={matrixDot}></span>
        {macLabel} LAYOUT MATRIX
      </div>
      {DURATIONS.map((d) => (
        <div key={d}>
          <div style={durLabel}>⏱ {d} MINUTES</div>
          <div style={cellRow}>
            {STYLES.map((s) => {
              const existing = findExisting(d, s.key)
              const fieldKey = `cell_${macKey}_${d}_${s.key}`
              const justUploaded = formData && formData[fieldKey]
              return (
                <div key={s.key} style={cellBox}>
                  <div style={cellStyleName}>{s.label}</div>
                  {existing && existing.filename && !justUploaded ? (

                    <a href={`${apiUploads}/${existing.filename}`}
                      target="_blank"
                      rel="noreferrer"
                      title={existing.filename}
                      style={{
                        fontSize: "0.58rem",
                        color: "#2E8B57",
                        background: "#E7F3EC",
                        borderRadius: 4,
                        padding: "2px 5px",
                        display: "block",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        marginBottom: 4,
                        maxWidth: "100%",
                        boxSizing: "border-box"
                      }}
                    >
                      ✓{" "}
                      {existing.filename.length > 18
                        ? existing.filename.slice(0, 8) +
                        "..." +
                        existing.filename.slice(-7)
                        : existing.filename}
                    </a>
                  ) : null}
                  <div
                    className="zone-cell-input"
                    style={{ flex: 1, minWidth: 0, maxWidth: "100%", overflow: "hidden" }}
                  >
                    <FileInput
                      source={`cell_${macKey}_${d}_${s.key}`}
                      label=""
                      placeholder={
                        <span style={{ color: "#b3b3b3", fontSize: "0.68rem" }}>
                          {existing ? "Replace" : "No file — click or drop"}
                        </span>
                      }
                    >
                      <ShortFileField source="src" title="title" />
                    </FileInput>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ))
      }
    </div >
  )
}

// ── ZoneMatrixInputs wrapper ──

export const ZoneMatrixInputs: React.FC<{
  existingCells?: any[]
  formData?: any
}> = ({ existingCells, formData }) => (
  <div style={matrixWrap}>
    <style>{`
      .zone-cell-input .previews {
        margin: 4px 0 0;
        width: 100%;
        box-sizing: border-box;
      }
      .zone-cell-input .previews ul,
      .zone-cell-input .previews li {
        list-style: none;
        margin: 0;
        padding: 0;
        width: 100%;
      }
      .zone-cell-input .previews > div,
      .zone-cell-input .previews li {
        display: flex;
        align-items: center;
        gap: 4px;
        width: 100%;
        box-sizing: border-box;
        overflow: hidden;
      }
      .zone-cell-input .previews a,
      .zone-cell-input .previews span,
      .zone-cell-input .previews p {
        font-size: 0.62rem;
        color: #1565C0;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        flex: 1 1 auto;
        min-width: 0;
        max-width: 100%;
        width: 100%;
        line-height: 1.3;
        margin: 0;
        display: block;
      }
      .zone-cell-input button {
        flex: 0 0 auto;
        padding: 2px;
        margin: 0;
        background: transparent !important;
        box-shadow: none !important;
      }
      .zone-cell-input button:hover,
      .zone-cell-input button:focus,
      .zone-cell-input button:active {
        background: transparent !important;
        box-shadow: none !important;
      }
      .zone-cell-input button .MuiTouchRipple-root {
        display: none !important;
      }
      .zone-cell-input button svg {
        width: 16px;
        height: 16px;
      }
      .zone-cell-input [class*="dropZone"] {
        padding: 8px;
        min-height: 44px;
        box-sizing: border-box;
      }
      .zone-cell-input label {
        display: none;
      }
    `}</style>
    {MAC_GROUPS.map((mg) => (
      <MatrixCard
        key={mg.key}
        macKey={mg.key}
        macLabel={mg.label}
        existingCells={existingCells}
        formData={formData}
      />
    ))}
  </div>
)
